import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getAuthService, isRealSignIn } from '@shared/services'
import { useAuth } from '@shared/state/AuthContext'
import { returnsAfterSignIn } from '../routes'
import { GooeyBackground } from '../components/GooeyBackground'
import { Lockup } from '../components/Lockup'
import '../components/lockup.css'
import '../login.css'
import { useDarkOnly } from '../theme'

/** Below this the lockup stacks, so there is no R to line up with. */
const ALIGN_MIN_VIEWPORT = 640
/** Keep the form usable even if the measurement lands somewhere odd. */
const FORM_MIN = 210
const FORM_MAX = 460

/**
 * Microsoft's four squares, drawn in one colour.
 *
 * The official mark is red, green, blue and yellow. This product is black and
 * white everywhere else, and the shape alone is recognisable at this size, so
 * it takes the button's own colour instead of importing a palette.
 */
function MicrosoftMark() {
  return (
    <svg className="login__ms-mark" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="0" y="0" width="7" height="7" fill="currentColor" />
      <rect x="9" y="0" width="7" height="7" fill="currentColor" />
      <rect x="0" y="9" width="7" height="7" fill="currentColor" />
      <rect x="9" y="9" width="7" height="7" fill="currentColor" />
    </svg>
  )
}

export function LoginPage() {
  const auth = getAuthService()
  const { session, loading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Drawn for black, whatever skin is chosen (theme.ts).
  useDarkOnly()

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const stageRef = useRef<HTMLElement>(null)
  const wonderRef = useRef<HTMLSpanElement>(null)

  /**
   * The lockup and the form are both centred on the page, and the form's right
   * edge sits under the R of WONDER. That only works at one particular width,
   * which depends on how wide the type actually renders — so measure it rather
   * than hard-coding a number that would be wrong in any other font.
   */
  const [formWidth, setFormWidth] = useState<number | null>(null)

  const measure = useCallback(() => {
    const wonder = wonderRef.current
    const stage = stageRef.current
    if (!wonder || !stage) return
    if (window.innerWidth < ALIGN_MIN_VIEWPORT) {
      setFormWidth(null)
      return
    }
    const stageBox = stage.getBoundingClientRect()
    const centre = stageBox.left + stageBox.width / 2
    const half = wonder.getBoundingClientRect().right - centre
    if (half <= 0) return
    setFormWidth(Math.round(Math.min(Math.max(half * 2, FORM_MIN), FORM_MAX)))
  }, [])

  useLayoutEffect(() => {
    measure()

    const observer = new ResizeObserver(measure)
    if (wonderRef.current) observer.observe(wonderRef.current)
    window.addEventListener('resize', measure)
    // Aptos may not have painted yet on the first frame
    document.fonts?.ready.then(measure).catch(() => undefined)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  // Signing in always lands on the overview, never on whatever page the guard
  // happened to intercept — the overview is where the work starts. Connect
  // Miro is the exception, because it has a job to finish; the prefix check
  // keeps any other value in that state from redirecting us (routes.ts).
  const HOME = '/'
  const state = location.state as { from?: string; error?: string } | null
  const from = state?.from ?? ''
  const destination = returnsAfterSignIn(from) ? from : HOME

  useEffect(() => {
    if (!loading && session) navigate(destination, { replace: true })
  }, [loading, session, navigate, destination])

  /**
   * A sign-in that came back from Microsoft refused arrives here carrying the
   * reason, because the callback page has nothing to show it on.
   */
  useEffect(() => {
    if (state?.error) setError(state.error)
  }, [state?.error])

  /**
   * Nothing is drawn until it is known whether anybody is signed in.
   *
   * The sign-in form used to render straight away and the redirect happened in
   * an effect just afterwards, so anyone already signed in saw the whole login
   * screen — logo, colour field and all — flash past on the way to the
   * overview. The check takes a moment; a blank moment is invisible, a wrong
   * screen is not.
   *
   * It has to come after every hook above: an early return that skips hooks
   * changes how many run between renders, which React refuses to accept.
   */
  const settling = loading || Boolean(session)

  async function handleSignIn(event: React.FormEvent) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await auth.signInWithMicrosoft()
      // Against the real tenant this line is never reached: the browser is
      // already on Microsoft's domain. Leaving `busy` set is deliberate, so
      // the button does not blink back to life during the navigation.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach Microsoft.')
      setBusy(false)
    }
  }

  if (settling) return <div className="auth-gate" />

  return (
    <GooeyBackground className="login">
      <main className="login__stage" ref={stageRef}>
        <Lockup wonderRef={wonderRef} />

        <div
          className="login__form-slot"
          style={formWidth ? { width: formWidth } : undefined}
        >
          <form className="login__form" onSubmit={handleSignIn} noValidate>
            <button type="submit" className="login__button" disabled={busy} autoFocus>
              <MicrosoftMark />
              <span>{busy ? 'Opening Microsoft…' : 'Sign in with Microsoft'}</span>
            </button>

            <p className="login__hint">
              {isRealSignIn()
                ? 'Use your WovenWorks work account.'
                : 'Offline mode · no account required'}
            </p>
          </form>

          <p className="login__error" role="alert" aria-live="polite">
            {error ?? ''}
          </p>
        </div>
      </main>
    </GooeyBackground>
  )
}
